import { useSyncExternalStore } from "react";
import {
  type ConnectivityEvent,
  type ConnectivityState,
  INITIAL_CONNECTIVITY_STATE,
  reduceConnectivity,
} from "./connectivity-state.ts";

type Listener = (state: ConnectivityState) => void;

function createConnectivityStore(initial: ConnectivityState = INITIAL_CONNECTIVITY_STATE) {
  let state = initial;
  const listeners = new Set<Listener>();

  return {
    getState(): ConnectivityState {
      return state;
    },
    dispatch(event: ConnectivityEvent): void {
      const next = reduceConnectivity(state, event);
      if (next === state) return;
      state = next;
      for (const listener of listeners) listener(state);
    },
    subscribe(listener: Listener): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    /** Test-only reset — production code never needs to rewind connectivity. */
    reset(next: ConnectivityState = INITIAL_CONNECTIVITY_STATE): void {
      state = next;
    },
  };
}

export type ConnectivityStore = ReturnType<typeof createConnectivityStore>;

/** Module-level singleton: one connectivity read for the whole app. */
export const connectivityStore = createConnectivityStore();

let windowListenersAttached = false;

/**
 * Wires `navigator.onLine`/`online`/`offline` browser signals into the
 * shared store. Idempotent — safe to call once at app boot (see `main.tsx`).
 * The other half of the signal (request outcomes) comes from
 * `lib/query-client.ts` (queries) and `features/connectivity/retry.ts`
 * (direct mutation calls).
 */
export function startConnectivityMonitoring(): void {
  if (windowListenersAttached || typeof window === "undefined") return;
  windowListenersAttached = true;

  if (window.navigator.onLine === false) {
    connectivityStore.dispatch({ type: "browser-offline" });
  }
  window.addEventListener("online", () => connectivityStore.dispatch({ type: "browser-online" }));
  window.addEventListener("offline", () => connectivityStore.dispatch({ type: "browser-offline" }));
}

/** React hook: current connectivity status, re-rendering the subscriber on change. */
export function useConnectivityStatus(): ConnectivityState["status"] {
  return useSyncExternalStore(
    connectivityStore.subscribe,
    () => connectivityStore.getState().status,
    () => "online",
  );
}
